import './EducationDetails.css'
import "react-datepicker/dist/react-datepicker.css";

import { SOMETHING_WENT_WRONG_MESSAGE, TRY_UPDATE_CHANGES_AGAIN_MESSAGE, UPDATE_SUCCESS_MESSAGE } from '../../common/constants';
import { getUserInfoAndToken, getYearFromDate } from '../../common/helper';
import { useEffect, useRef, useState } from 'react';

import Button from '../../components/button/Button';
import ConfirmDeletionModal from './ConfirmDeletionModal';
import DatePicker from "react-datepicker";
import Loader from '../../components/loader/Loader';
import Select from 'react-select';
import Toast from '../../components/toast/Toast';
import analytics from '../../firebase';
import { logEvent } from "firebase/analytics";
import { updateEducationDetails } from '../../services/ProfileService';

const EducationDetails = ({ user, codeTableEducationLevelOptions }) => {
    const [newForm, setNewForm] = useState(false);
    const [selectedEducationLevel, setSelectedEducationLevel] = useState('');
    const [educationLevelErrorMessage, setEducationLevelErrorMessage] = useState('');
    const [certificationName, setCertificationName] = useState('');
    const [certificationNameErrorMessage, setCertificationNameErrorMessage] = useState('');
    const [institute, setInstitute] = useState('');
    const [instituteErrorMessage, setInstituteErrorMessage] = useState('');
    const [fromEducationPeriod, setFromEducationPeriod] = useState('');
    const [fromEducationPeriodErrorMessage, setFromEducationPeriodErrorMessage] = useState('');
    const [toEducationPeriod, setToEducationPeriod] = useState('');
    const [toEducationPeriodErrorMessage, setToEducationPeriodErrorMessage] = useState('');
    const [currentlyStudying, setCurrentlyStudying] = useState(false);
    const [educationDetails, setEducationDetails] = useState([]);
    const [backDateErrorMessage, setBackDateErrorMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessageHeader, setErrorMessageHeader] = useState('');
    const [errorMessageDescription, setErrorMessageDescription] = useState('');
    const [successMessageHeader, setSuccessMessageHeader] = useState('');
    const [successMessageDescription, setSuccessMessageDescription] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [deleteIndex, setDeleteIndex] = useState(0);

    const educationDetailsLisRef = useRef([]);

    const currentDate = new Date();

    useEffect(() => {
        if (user?.edu) {
            const existingEducationDetails = transformEducationDetailsForDisplay(user?.edu);
            setEducationDetails(existingEducationDetails);
        }
    }, [user, codeTableEducationLevelOptions])

    const handleCertificationNameChange = (e) => {
        setCertificationNameErrorMessage('');
        setCertificationName(e.target.value);
    }
    const handleInstituteChange = (e) => {
        setInstituteErrorMessage('');
        setInstitute(e.target.value);
    }
    const handleFromEducationPeriodChange = (date) => {
        setFromEducationPeriodErrorMessage('');
        setBackDateErrorMessage('');
        setFromEducationPeriod(date);
    }
    const handleToEducationPeriodChange = (date) => {
        if (date) {
            setToEducationPeriodErrorMessage('');
        }
        setToEducationPeriod(date);
    }
    const handleCurrentlyStudyingChange = () => {
        setBackDateErrorMessage('');
        setCurrentlyStudying(!currentlyStudying);
    }

    const handleEducationLevelChange = (selectedItem) => {
        setEducationLevelErrorMessage('');
        setSelectedEducationLevel(selectedItem);
    }

    const validateForm = () => {
        if (!selectedEducationLevel) {
            setEducationLevelErrorMessage('Please specify your education level.');
        }
        if (!certificationName) {
            setCertificationNameErrorMessage('Please specify the certification name.');
        }
        if (!institute) {
            setInstituteErrorMessage('Please specify your school or institute.');
        }
        if (!fromEducationPeriod) {
            setFromEducationPeriodErrorMessage('Please specify the start year.');
        }
        if (!currentlyStudying && !toEducationPeriod) {
            setToEducationPeriodErrorMessage('Please specify the year of completion.');
        }
    }

    const resetFieldValues = () => {
        setSelectedEducationLevel('');
        setCertificationName('');
        setInstitute('');
        setFromEducationPeriod('');
        setToEducationPeriod('');
        setCurrentlyStudying(false);
    }

    const resetErrors = () => {
        setEducationLevelErrorMessage('');
        setCertificationNameErrorMessage('');
        setInstituteErrorMessage('');
        setFromEducationPeriodErrorMessage('');
        setToEducationPeriodErrorMessage('');
        setBackDateErrorMessage('');
    }

    const handleCancelClick = () => {
        setNewForm(false);
        resetErrors();
        resetFieldValues();
    }

    const invalidDates = (startDate, endDate) => {
        if (startDate && endDate) {
            return new Date(startDate) >= new Date(endDate)
        }
        return false
    }

    const transformEducationDetailsForDisplay = (edu) => {
        return edu.map((e) => {
            return {
                educationLevel: e.edulvlc,
                title: e.certname,
                subTitle: e.inst,
                fromDate: e.yearstart,
                toDate: e.yearend,
                currentlyStudying: (e.isprsnt === '1')
            }
        });
    };

    const transformEducationDetailsForAPI = (educationDetail) => {
        return {
            edulvl: educationDetail.educationLevel,
            cert: educationDetail.title,
            inst: educationDetail.subTitle,
            start: educationDetail.fromDate ? getYearFromDate(educationDetail.fromDate) : '',
            end: educationDetail.toDate ? getYearFromDate(educationDetail.toDate) : '',
            present: educationDetail.currentlyStudying ? '1' : '0'
        }
    };

    const resetSuccessMessages = () => {
        setSuccessMessageHeader('');
        setSuccessMessageDescription('');
    }

    const resetErrorMessages = () => {
        setErrorMessageHeader('');
        setErrorMessageDescription('');
    }

    const generateCSS = () => {
        try {
            const styleTag = document.createElement('style');
            let css = '';

            // Calculate each li element content height
            const heights = [];
            educationDetailsLisRef.current.forEach((li) => {
                if (li) {
                    const titleHeight = li.querySelector('.fab-ed-list-title').clientHeight;
                    const subTitleHeight = li.querySelector('.fab-ed-list-subtitle').clientHeight;
                    const dateRangeHeight = li.querySelector('.fab-ed-list-date-range').clientHeight;
                    let totalHeight = (titleHeight + subTitleHeight + dateRangeHeight);
                    heights.push(totalHeight);
                }
            });

            // Adjust vertical line height as per next li element
            heights.forEach((_, index) => {
                if (index + 1 !== heights.length) {
                    let current = heights[index];
                    let next = heights[index + 1];
                    let total = current + (next / 2) - (current / 2) + 10;
                    css += `.fab-ed-list-container li:nth-child(${index + 1})::before { height: ${total}px; }`;
                }
            })

            styleTag.innerHTML = css;
            document.head.appendChild(styleTag);
        } catch (error) {
            console.log(`error occurred while generating css: ${error}`)
        }
    };

    useEffect(() => {
        generateCSS();
    }, [educationDetails]);

    const handleDeleteClick = async () => {
        try {
            resetSuccessMessages();
            resetErrorMessages();
            setIsLoading(true);

            const educationDetailsToUpdate = educationDetails.filter((_, i) => i !== deleteIndex)
                .map((educationDetail) => transformEducationDetailsForAPI(educationDetail));

            const { userId, username, token } = getUserInfoAndToken();
            const response = await updateEducationDetails(userId, username, token, educationDetailsToUpdate);
            if (response?.code === '00') {
                const educations = response?.data;
                setEducationDetails(transformEducationDetailsForDisplay(educations));

                setSuccessMessageHeader(UPDATE_SUCCESS_MESSAGE);
                setSuccessMessageDescription('Your changes to Education details has been saved!');
            } else {
                const messages = response?.message;
                if (messages?.length > 0) {
                    setErrorMessageHeader('Update error');
                    setErrorMessageDescription(messages.join('\n'));
                }
            }
            setShowModal(false)
            setNewForm(false);
            setIsLoading(false);
        } catch (error) {
            console.log(`error occurred while deleting the education details: ${error}`);
            setShowModal(false)
            setIsLoading(false);
            setErrorMessageHeader(SOMETHING_WENT_WRONG_MESSAGE);
            setErrorMessageDescription(TRY_UPDATE_CHANGES_AGAIN_MESSAGE);
        }
    }

    const handleDelete = (index) => {
        setShowModal(true)
        setDeleteIndex(index)
    }

    const handleSaveClick = async () => {
        resetSuccessMessages();
        resetErrorMessages();
        validateForm();

        const invalid = invalidDates(fromEducationPeriod, toEducationPeriod);
        if (!currentlyStudying && invalid) {
            setBackDateErrorMessage('Start date cannot be later than end date.');
            return
        }

        if (selectedEducationLevel && certificationName && institute && fromEducationPeriod && (toEducationPeriod || currentlyStudying)) {
            try {
                setIsLoading(true);

                const educationDetailsToUpdate = [...educationDetails, {
                    educationLevel: selectedEducationLevel.codeid,
                    title: certificationName,
                    subTitle: institute,
                    fromDate: fromEducationPeriod,
                    toDate: toEducationPeriod,
                    currentlyStudying,
                }].map((educationDetail) => transformEducationDetailsForAPI(educationDetail));

                const { userId, username, token } = getUserInfoAndToken();
                const response = await updateEducationDetails(userId, username, token, educationDetailsToUpdate);
                if (response?.code === '00') {
                    const eDetails = response?.data;
                    logEvent(analytics, 'profile_education_updated', { 'userid': userId, 'education_level': selectedEducationLevel });
                    setEducationDetails(transformEducationDetailsForDisplay(eDetails));

                    setNewForm(false);
                    resetErrors();
                    resetFieldValues();
                    resetErrorMessages();
                    setSuccessMessageHeader(UPDATE_SUCCESS_MESSAGE);
                    setSuccessMessageDescription('Your changes to Education details has been saved!');
                } else {
                    const messages = response?.message;
                    if (messages?.length > 0) {
                        if (Array.isArray(messages)) {
                            setErrorMessageHeader('Update error');
                            setErrorMessageDescription(messages.join('\n'));
                        } else {
                            setErrorMessageHeader('Update error');
                            setErrorMessageDescription(messages);
                        }
                    }
                    setNewForm(true);
                }

                setIsLoading(false);
            } catch (error) {
                console.log(`error occurred while saving the education details: ${error}`);

                setNewForm(true);
                setIsLoading(false);
                setErrorMessageHeader(SOMETHING_WENT_WRONG_MESSAGE);
                setErrorMessageDescription(TRY_UPDATE_CHANGES_AGAIN_MESSAGE);
            }
        }
    }

    const addButtonClicked = () => {
        setNewForm(true)
        logEvent(analytics, 'profile_education_initiated');
    }


    return (
        <>
            {
                (successMessageHeader && successMessageDescription) && <Toast
                    type='success'
                    header={successMessageHeader}
                    message={successMessageDescription}
                    onClose={resetSuccessMessages}
                />
            }
            {
                (errorMessageHeader && errorMessageDescription) && <Toast
                    type='error'
                    header={errorMessageHeader}
                    message={errorMessageDescription}
                    onClose={resetErrorMessages}
                />
            }
            {isLoading && <Loader></Loader>}
            <ConfirmDeletionModal showModal={showModal} setShowModal={setShowModal} handleConfirm={handleDeleteClick} />
            {/* <div className='fb-ed-section'> */}
            <>
                <div className='fb-header-container'>
                    <div className='fb-header-btn-wrapper'>
                        <p className='fb-section-title'>Education details</p>
                        <Button className='fb-add-btn' onClick={addButtonClicked}>
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path fillRule="evenodd" clipRule="evenodd" d="M7 0.125C7.34518 0.125 7.625 0.404822 7.625 0.75V6.375H13.25C13.5952 6.375 13.875 6.65482 13.875 7C13.875 7.34518 13.5952 7.625 13.25 7.625H7.625V13.25C7.625 13.5952 7.34518 13.875 7 13.875C6.65482 13.875 6.375 13.5952 6.375 13.25V7.625H0.75C0.404822 7.625 0.125 7.34518 0.125 7C0.125 6.65482 0.404822 6.375 0.75 6.375H6.375V0.75C6.375 0.404822 6.65482 0.125 7 0.125Z" fill="#D31145" />
                            </svg>
                            <p className='fb-add-btn-text'>Add</p>
                        </Button>
                    </div>
                    <p className='fb-description'>Add your educational background to your profile.</p>
                </div>
                {
                    educationDetails.length > 0 && <div className='fab-ed-list-container'>
                        <ul>
                            {educationDetails.map((value, index) => (
                                <li key={index} ref={el => educationDetailsLisRef.current[index] = el}>
                                    <svg className='list-icon' width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <circle cx="6" cy="6" r="5.25" stroke="#D31145" strokeWidth="1.5" />
                                    </svg>
                                    <div className='fb-list-contents-wrapper'>
                                        <label className='fab-ed-list-title'>{value.title}</label>
                                        <label className='fab-ed-list-subtitle'>{value.subTitle}</label>
                                        <label className='fab-ed-list-date-range'>{`${value.fromDate} - ${value.currentlyStudying ? 'present' : value.toDate}`}</label>
                                    </div>
                                    <div className='fb-list-actions-container'>
                                        <Button className='fb-list-btn-wrapper' onClick={() => handleDelete(index)}>
                                            <svg width="18" height="22" viewBox="0 0 18 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path fillRule="evenodd" clipRule="evenodd" d="M9.00012 2C8.45247 2 7.90689 2.00876 7.36352 2.02614C6.6089 2.05028 6.00012 2.68393 6.00012 3.47819V3.59082C6.99249 3.53056 7.99278 3.5 9.00012 3.5C10.0075 3.5 11.0078 3.53056 12.0001 3.59082V3.47819C12.0001 2.68393 11.3913 2.05028 10.6367 2.02614C10.0934 2.00876 9.54778 2 9.00012 2ZM13.5001 3.70498V3.47819C13.5001 1.91371 12.2876 0.578183 10.6847 0.526907C10.1253 0.509014 9.56377 0.5 9.00012 0.5C8.43648 0.5 7.87491 0.509014 7.31556 0.526907C5.71265 0.578183 4.50012 1.91371 4.50012 3.47819V3.70498C3.5454 3.79237 2.59858 3.90731 1.66048 4.04898C1.31315 4.10143 0.96701 4.15755 0.622112 4.2173C0.213976 4.28799 -0.0595705 4.67617 0.011128 5.0843C0.0818265 5.49244 0.469998 5.76598 0.878134 5.69529C0.947738 5.68323 1.0174 5.67132 1.0871 5.65957L2.09253 18.7301C2.21276 20.2931 3.51608 21.5 5.08369 21.5H12.9166C14.4842 21.5 15.7875 20.2931 15.9077 18.7301L16.9131 5.65957C16.9829 5.67132 17.0525 5.68323 17.1221 5.69529C17.5302 5.76598 17.9184 5.49244 17.9891 5.0843C18.0598 4.67617 17.7863 4.28799 17.3781 4.2173C17.0332 4.15755 16.6871 4.10143 16.3398 4.04898C15.4017 3.90731 14.4549 3.79237 13.5001 3.70498ZM15.4261 5.43321C14.5228 5.3104 13.6113 5.21311 12.6923 5.14209C11.4741 5.04796 10.2428 5 9.00012 5C7.75746 5 6.52614 5.04796 5.30791 5.14209C4.38898 5.21311 3.47747 5.3104 2.57412 5.43321L3.58811 18.615C3.64823 19.3965 4.29989 20 5.08369 20H12.9166C13.7004 20 14.352 19.3965 14.4121 18.615L15.4261 5.43321ZM6.23091 7.25055C6.64482 7.23463 6.99326 7.55727 7.00918 7.97118L7.35534 16.9712C7.37126 17.3851 7.04862 17.7335 6.63472 17.7494C6.22081 17.7654 5.87237 17.4427 5.85645 17.0288L5.51029 8.02882C5.49437 7.61492 5.81701 7.26647 6.23091 7.25055ZM11.7693 7.25055C12.1832 7.26647 12.5059 7.61492 12.49 8.02882L12.1438 17.0288C12.1279 17.4427 11.7794 17.7654 11.3655 17.7494C10.9516 17.7335 10.629 17.3851 10.6449 16.9712L10.9911 7.97118C11.007 7.55727 11.3554 7.23463 11.7693 7.25055Z" fill="#B91C1C" />
                                            </svg>
                                        </Button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                }
                {
                    newForm &&
                    <div className='fb-ed-form-container'>
                        <div className='fb-form-section-wrapper'>
                            <div className='fb-form-section-header'>
                                <label className='fb-form-section-header-text'>Education level<span>*</span></label>
                            </div>
                            <div className={`fab-dropdown ${educationLevelErrorMessage ? 'fb-form-section-dropdown-error' : ''}`}>
                                <Select
                                    options={codeTableEducationLevelOptions}
                                    isMulti={false}
                                    getOptionValue={(option) => option.val}
                                    getOptionLabel={(option) => option.val}
                                    placeholder='Please select your education level'
                                    onChange={handleEducationLevelChange}
                                    value={selectedEducationLevel}
                                />
                            </div>
                            {educationLevelErrorMessage && <label className='fab-form-field-error-message'>{educationLevelErrorMessage}</label>}
                        </div>
                        <div className='fb-form-section-wrapper'>
                            <div className='fb-form-section-header'>
                                <label className='fb-form-section-header-text'>Certification name<span>*</span></label>
                            </div>
                            <input type='text' className={`fb-form-section-input ${certificationNameErrorMessage ? 'fb-form-section-input-error' : ''} fb-form-section-input-text`} placeholder='E.g., Certificate in Engineering' defaultValue={certificationName} value={certificationName} onChange={handleCertificationNameChange} />
                            {certificationNameErrorMessage && <label className='fab-form-field-error-message'>{certificationNameErrorMessage}</label>}
                        </div>
                        <div className='fb-form-section-wrapper'>
                            <div className='fb-form-section-header'>
                                <label className='fb-form-section-header-text'>Institute<span>*</span></label>
                            </div>
                            <input type='text' className={`fb-form-section-input ${instituteErrorMessage ? 'fb-form-section-input-error' : ''} fb-form-section-input-text`} placeholder='E.g., Singapore Polytechnic' defaultValue={institute} value={institute} onChange={handleInstituteChange} />
                            {instituteErrorMessage && <label className='fab-form-field-error-message'>{instituteErrorMessage}</label>}
                        </div>
                        <div className='fb-form-section-wrapper'>
                            <div className='fb-form-section-header'>
                                <label className='fb-form-section-header-text'>Education Period</label>
                            </div>
                            <div className={`fb-form-wp-from-to-container ${(fromEducationPeriodErrorMessage || toEducationPeriodErrorMessage || backDateErrorMessage) ? 'fb-form-wp-from-to-container-height' : ''}`}>
                                <div className='fb-form-wp-date-wrapper'>
                                    <div className='fb-form-wp-date-header'>
                                        <label className='fb-form-wp-date-header-text'>Start year<span>*</span></label>
                                    </div>
                                    <div className='fb-form-wp-date-picker-container'>
                                        <DatePicker
                                            className={`fb-form-wp-date-picker-input-wrapper ${fromEducationPeriodErrorMessage ? 'fb-form-wp-date-picker-input-wrapper-error' : ''} fb-form-wp-date-picker-input-text`}
                                            placeholderText='YYYY'
                                            dateFormat='yyyy'
                                            selected={fromEducationPeriod}
                                            onChange={(date) => handleFromEducationPeriodChange(date)}
                                            maxDate={currentDate}
                                            showYearPicker
                                            scrollableYearDropdown
                                        />
                                    </div>
                                    {fromEducationPeriodErrorMessage && <label className='fab-form-field-error-message'>{fromEducationPeriodErrorMessage}</label>}
                                    {backDateErrorMessage && <label className='fab-form-field-error-message'>{backDateErrorMessage}</label>}
                                </div>
                                <div className='fb-form-wp-date-wrapper'>
                                    <div className='fb-form-wp-date-header'>
                                        <label className='fb-form-wp-date-header-text'>End year<span>*</span></label>
                                    </div>
                                    <div className='fb-form-wp-date-picker-container'>
                                        <DatePicker
                                            className={`fb-form-wp-date-picker-input-wrapper ${(!currentlyStudying && toEducationPeriodErrorMessage) ? 'fb-form-wp-date-picker-input-wrapper-error' : ''} fb-form-wp-date-picker-input-text`}
                                            placeholderText={currentlyStudying ? 'present' : 'YYYY'}
                                            dateFormat='yyyy'
                                            selected={currentlyStudying ? '' : toEducationPeriod}
                                            onChange={(date) => handleToEducationPeriodChange(date)}
                                            maxDate={currentDate}
                                            showYearPicker
                                            scrollableYearDropdown
                                            disabled={currentlyStudying}
                                        />
                                    </div>
                                    {(!currentlyStudying && toEducationPeriodErrorMessage) && <label className='fab-form-field-error-message'>{toEducationPeriodErrorMessage}</label>}
                                </div>
                            </div>
                            <div className='fb-form-wp-cwh-container'>
                                <div className='fb-form-wp-cwh-cb-wrapper'>
                                    <input type='checkbox' className='fb-form-wp-cwh-cb' checked={currentlyStudying} onChange={handleCurrentlyStudyingChange} />
                                    <p className='fb-form-wp-cwh-cb-text'>I am currently studying</p>
                                </div>
                            </div>
                        </div>
                        <div className='fb-save-cancel-container'>
                            <Button className='fb-cancel-btn fb-cancel-btn-text' onClick={handleCancelClick}>
                                Cancel
                            </Button>
                            <Button className='fb-save-btn fb-save-btn-text' onClick={handleSaveClick}>
                                Save
                            </Button>
                        </div>
                    </div>
                }
                {/* </div> */}
            </>
        </>
    )
}

export default EducationDetails;